import { useState } from "react";

const topics = [
  {
    id: "super",
    title: "super()",
    subtitle: "What it does & how it works",
    icon: "⬆",
    color: "#f59e0b",
    sections: [
      {
        heading: "What is super()?",
        body: "super() gives you access to the next class in the MRO (Method Resolution Order). It doesn't always mean 'parent' — it means 'next in line'.",
        code: `class Animal:
    def __init__(self, name):
        self.name = name

class Dog(Animal):
    def __init__(self, name, breed):
        super().__init__(name)  # calls Animal.__init__
        self.breed = breed

d = Dog("Rex", "Labrador")
print(d.name)   # Rex
print(d.breed)  # Labrador`,
        output: "Rex\nLabrador",
        note: "super().__init__(name) calls Animal's __init__ so Dog doesn't have to repeat the logic."
      },
      {
        heading: "Without super() — the problem",
        body: "If you skip super(), the parent's __init__ never runs. Your object is missing attributes.",
        code: `class Animal:
    def __init__(self, name):
        self.name = name

class Dog(Animal):
    def __init__(self, name, breed):
        # forgot super().__init__(name)
        self.breed = breed

d = Dog("Rex", "Labrador")
print(d.breed)  # Labrador
print(d.name)   # ❌ AttributeError!`,
        output: "Labrador\nAttributeError: 'Dog' has no attribute 'name'",
        note: "Without super(), Animal.__init__() never ran, so self.name was never set."
      },
      {
        heading: "super() follows the MRO, not the parent",
        body: "In multiple inheritance, super() calls the NEXT class in MRO — which may not be the direct parent.",
        code: `class A:
    def greet(self):
        print("A")

class B(A):
    def greet(self):
        print("B")
        super().greet()  # next in MRO

class C(A):
    def greet(self):
        print("C")
        super().greet()  # next in MRO

class D(B, C):
    def greet(self):
        print("D")
        super().greet()  # next in MRO

D().greet()
# MRO: D → B → C → A`,
        output: "D\nB\nC\nA",
        note: "B's super() calls C (not A!) because C is next in D's MRO. Each class is called exactly once."
      }
    ]
  },
  {
    id: "single",
    title: "Single Inheritance",
    subtitle: "A → B (one parent)",
    icon: "│",
    color: "#3b82f6",
    sections: [
      {
        heading: "The simplest form",
        body: "One child inherits from one parent. A straight chain. super() always goes to the one parent above.",
        code: `class Vehicle:
    def __init__(self, brand):
        self.brand = brand
    def start(self):
        return f"{self.brand} engine started"

class Car(Vehicle):
    def __init__(self, brand, doors):
        super().__init__(brand)
        self.doors = doors

c = Car("Toyota", 4)
print(c.start())   # inherited from Vehicle
print(c.doors)     # Car's own attribute`,
        output: "Toyota engine started\n4",
        note: "Car inherits start() from Vehicle without rewriting it."
      },
      {
        heading: "MRO in single inheritance",
        body: "The MRO is simply: Child → Parent → object",
        code: `class A:
    pass
class B(A):
    pass
class C(B):
    pass

print(C.mro())
# [C, B, A, object]`,
        output: "[C, B, A, object]",
        note: "A straight chain — no ambiguity. super() just goes one level up."
      }
    ]
  },
  {
    id: "multi",
    title: "Multiple Inheritance",
    subtitle: "A child, many parents",
    icon: "⑃",
    color: "#8b5cf6",
    sections: [
      {
        heading: "What is it?",
        body: "A class inherits from TWO or more parents. Python supports this — many languages don't.",
        code: `class Flyer:
    def fly(self):
        return "I can fly!"

class Swimmer:
    def swim(self):
        return "I can swim!"

class Duck(Flyer, Swimmer):
    pass

d = Duck()
print(d.fly())    # from Flyer
print(d.swim())   # from Swimmer`,
        output: "I can fly!\nI can swim!",
        note: "Duck gets abilities from BOTH parents. Order matters: Flyer is checked before Swimmer."
      },
      {
        heading: "super() with multiple parents",
        body: "When both parents have the same method, super() follows MRO (left parent first).",
        code: `class Flyer:
    def move(self):
        return "Flying"

class Swimmer:
    def move(self):
        return "Swimming"

class Duck(Flyer, Swimmer):
    pass

d = Duck()
print(d.move())  # "Flying" — Flyer is first
print(Duck.mro())
# [Duck, Flyer, Swimmer, object]`,
        output: "Flying\n[Duck, Flyer, Swimmer, object]",
        note: "Flyer is listed first in class Duck(Flyer, Swimmer), so Flyer.move() wins."
      }
    ]
  },
  {
    id: "multilevel",
    title: "Multilevel Inheritance",
    subtitle: "A → B → C (chain)",
    icon: "⇣",
    color: "#10b981",
    sections: [
      {
        heading: "A chain of inheritance",
        body: "Each class inherits from the one above it — like a family tree going grandparent → parent → child.",
        code: `class Animal:
    def __init__(self, name):
        self.name = name
        print(f"Animal init: {name}")

class Dog(Animal):
    def __init__(self, name, breed):
        super().__init__(name)
        self.breed = breed
        print(f"Dog init: {breed}")

class Puppy(Dog):
    def __init__(self, name, breed, toy):
        super().__init__(name, breed)
        self.toy = toy
        print(f"Puppy init: {toy}")

p = Puppy("Max", "Lab", "Ball")`,
        output: "Animal init: Max\nDog init: Lab\nPuppy init: Ball",
        note: "super() chains all the way up: Puppy → Dog → Animal. Each __init__ runs in order."
      },
      {
        heading: "MRO in multilevel",
        body: "MRO follows the chain exactly:",
        code: `class A:
    pass
class B(A):
    pass
class C(B):
    pass
class D(C):
    pass

print(D.mro())
# [D, C, B, A, object]`,
        output: "[D, C, B, A, object]",
        note: "Straight chain — D → C → B → A → object. No ambiguity."
      }
    ]
  },
  {
    id: "diamond",
    title: "Diamond Inheritance",
    subtitle: "The classic problem",
    icon: "◇",
    color: "#ef4444",
    sections: [
      {
        heading: "What is the Diamond Problem?",
        body: "When two parents share a common grandparent, it forms a diamond shape. Without MRO, the grandparent would be called TWICE.",
        code: `class A:
    def __init__(self):
        print("A init")

class B(A):
    def __init__(self):
        print("B init")
        super().__init__()

class C(A):
    def __init__(self):
        print("C init")
        super().__init__()

class D(B, C):
    def __init__(self):
        print("D init")
        super().__init__()

D()
# MRO: D → B → C → A → object`,
        output: "D init\nB init\nC init\nA init",
        note: "A is called only ONCE! Without MRO, A would run twice (once from B, once from C). Python's C3 linearization prevents this."
      },
      {
        heading: "Why super() is crucial here",
        body: "If you use Parent.__init__(self) instead of super(), the diamond breaks — A gets called twice.",
        code: `class A:
    def __init__(self):
        print("A init")

class B(A):
    def __init__(self):
        print("B init")
        A.__init__(self)  # hardcoded!

class C(A):
    def __init__(self):
        print("C init")
        A.__init__(self)  # hardcoded!

class D(B, C):
    def __init__(self):
        print("D init")
        B.__init__(self)
        C.__init__(self)

D()`,
        output: "D init\nB init\nA init\nC init\nA init  ← DUPLICATE!",
        note: "A runs TWICE! This is why super() exists — it follows MRO and ensures each class runs exactly once."
      }
    ]
  },
  {
    id: "hierarchical",
    title: "Hierarchical Inheritance",
    subtitle: "One parent, many children",
    icon: "⑂",
    color: "#ec4899",
    sections: [
      {
        heading: "Multiple children, one parent",
        body: "Several classes inherit from the same base class. Each child is independent — they share the parent but not each other.",
        code: `class Shape:
    def __init__(self, color):
        self.color = color

class Circle(Shape):
    def __init__(self, color, radius):
        super().__init__(color)
        self.radius = radius
    def area(self):
        return 3.14 * self.radius ** 2

class Square(Shape):
    def __init__(self, color, side):
        super().__init__(color)
        self.side = side
    def area(self):
        return self.side ** 2

c = Circle("red", 5)
s = Square("blue", 4)
print(c.area())   # 78.5
print(s.area())   # 16`,
        output: "78.5\n16",
        note: "Circle and Square both get 'color' from Shape via super(), but have their own area() logic."
      }
    ]
  },
  {
    id: "hybrid",
    title: "Hybrid Inheritance",
    subtitle: "Mix of all types",
    icon: "⬡",
    color: "#06b6d4",
    sections: [
      {
        heading: "Combining multiple patterns",
        body: "Hybrid inheritance mixes two or more types — e.g., multilevel + multiple. This is where MRO really earns its keep.",
        code: `class A:
    def show(self):
        print("A")

class B(A):          # single
    def show(self):
        print("B")
        super().show()

class C(A):          # single
    def show(self):
        print("C")
        super().show()

class D(B):          # multilevel
    def show(self):
        print("D")
        super().show()

class E(D, C):       # multiple + multilevel = hybrid
    def show(self):
        print("E")
        super().show()

E().show()
print(E.mro())`,
        output: "E\nD\nB\nC\nA\n[E, D, B, C, A, object]",
        note: "MRO handles the complexity: E → D → B → C → A. Every class appears exactly once, in the right order."
      }
    ]
  }
];

const CodeBlock = ({ code, output }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{
      background: "#1e1e2e",
      borderRadius: "10px 10px 0 0",
      padding: "6px 14px",
      display: "flex",
      gap: 6,
      alignItems: "center"
    }}>
      <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#ff5f57" }} />
      <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#febc2e" }} />
      <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#28c840" }} />
      <span style={{ marginLeft: 8, color: "#6c7086", fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>python</span>
    </div>
    <pre style={{
      background: "#181825",
      color: "#cdd6f4",
      padding: "16px 18px",
      margin: 0,
      fontSize: 13,
      lineHeight: 1.7,
      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
      overflowX: "auto",
      borderRadius: output ? 0 : "0 0 10px 10px",
      borderLeft: "3px solid #45475a"
    }}>{code}</pre>
    {output && (
      <div style={{
        background: "#11111b",
        padding: "10px 18px",
        borderRadius: "0 0 10px 10px",
        borderLeft: "3px solid #a6e3a1",
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 12,
        lineHeight: 1.6,
        color: "#a6e3a1"
      }}>
        <span style={{ color: "#6c7086", fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}>Output</span>
        <pre style={{ margin: "4px 0 0", whiteSpace: "pre-wrap" }}>{output}</pre>
      </div>
    )}
  </div>
);

const NoteBox = ({ text }) => (
  <div style={{
    background: "linear-gradient(135deg, #1e1b4b11, #312e8122)",
    border: "1px solid #6366f133",
    borderLeft: "4px solid #818cf8",
    borderRadius: 8,
    padding: "12px 16px",
    margin: "12px 0",
    fontSize: 13,
    lineHeight: 1.6,
    color: "#334155"
  }}>
    <span style={{ fontWeight: 700, color: "#6366f1", marginRight: 6 }}>💡</span> {text}
  </div>
);

export default function PythonInheritance() {
  const [active, setActive] = useState("super");
  const current = topics.find(t => t.id === active);

  return (
    <div style={{
      minHeight: "100vh",
      background: "#f8fafc",
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    }}>
      {/* Header */}
      <div style={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)",
        padding: "32px 24px 24px",
        textAlign: "center",
        position: "relative",
        overflow: "hidden"
      }}>
        <div style={{
          position: "absolute", inset: 0, opacity: 0.07,
          backgroundImage: "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
          backgroundSize: "32px 32px"
        }} />
        <h1 style={{
          color: "#f1f5f9",
          fontSize: 26,
          fontWeight: 700,
          margin: 0,
          letterSpacing: "-0.5px",
          position: "relative"
        }}>
          Python Inheritance & <span style={{ color: "#fbbf24" }}>super()</span>
        </h1>
        <p style={{ color: "#94a3b8", fontSize: 14, margin: "6px 0 0", position: "relative" }}>
          Interactive guide to all types of inheritance
        </p>
      </div>

      {/* Navigation Pills */}
      <div style={{
        display: "flex",
        gap: 6,
        padding: "16px 16px 8px",
        overflowX: "auto",
        flexWrap: "wrap",
        justifyContent: "center"
      }}>
        {topics.map(t => (
          <button
            key={t.id}
            onClick={() => setActive(t.id)}
            style={{
              padding: "8px 14px",
              border: active === t.id ? `2px solid ${t.color}` : "2px solid #e2e8f0",
              borderRadius: 99,
              background: active === t.id ? `${t.color}12` : "white",
              color: active === t.id ? t.color : "#64748b",
              fontWeight: active === t.id ? 700 : 500,
              fontSize: 12.5,
              cursor: "pointer",
              transition: "all 0.2s",
              fontFamily: "inherit",
              whiteSpace: "nowrap",
              display: "flex",
              alignItems: "center",
              gap: 5
            }}
          >
            <span style={{ fontSize: 14 }}>{t.icon}</span>
            {t.title}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: "12px 16px 40px", maxWidth: 720, margin: "0 auto" }}>
        {/* Topic Header */}
        <div style={{
          background: "white",
          borderRadius: 14,
          padding: "20px 22px",
          marginBottom: 16,
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          borderTop: `4px solid ${current.color}`
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{
              fontSize: 28,
              width: 48,
              height: 48,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `${current.color}15`,
              borderRadius: 12,
              color: current.color
            }}>{current.icon}</span>
            <div>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#0f172a" }}>
                {current.title}
              </h2>
              <p style={{ margin: "2px 0 0", fontSize: 13, color: "#94a3b8" }}>
                {current.subtitle}
              </p>
            </div>
          </div>
        </div>

        {/* Sections */}
        {current.sections.map((sec, i) => (
          <div key={i} style={{
            background: "white",
            borderRadius: 14,
            padding: "20px 22px",
            marginBottom: 14,
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)"
          }}>
            <h3 style={{
              margin: "0 0 8px",
              fontSize: 16,
              fontWeight: 700,
              color: "#1e293b",
              display: "flex",
              alignItems: "center",
              gap: 8
            }}>
              <span style={{
                width: 24,
                height: 24,
                borderRadius: 7,
                background: `${current.color}18`,
                color: current.color,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 700
              }}>{i + 1}</span>
              {sec.heading}
            </h3>
            <p style={{
              color: "#475569",
              fontSize: 14,
              lineHeight: 1.7,
              margin: "0 0 14px"
            }}>{sec.body}</p>
            <CodeBlock code={sec.code} output={sec.output} />
            {sec.note && <NoteBox text={sec.note} />}
          </div>
        ))}

        {/* Conditional diagrams */}
        {active === "diamond" && (
          <div style={{
            background: "white", borderRadius: 14, padding: "20px 22px",
            marginBottom: 14, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", textAlign: "center"
          }}>
            <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700, color: "#1e293b" }}>
              Diamond Shape Visualized
            </h3>
            <pre style={{
              fontFamily: "'JetBrains Mono', monospace", fontSize: 14, lineHeight: 2,
              color: "#334155", display: "inline-block", textAlign: "center"
            }}>{`       A          ← common ancestor
      / \\
     B   C        ← both inherit from A
      \\ /
       D          ← inherits from B and C

  MRO: D → B → C → A → object
  (A appears only ONCE)`}</pre>
          </div>
        )}

        {active === "single" && (
          <div style={{
            background: "white", borderRadius: 14, padding: "20px 22px",
            marginBottom: 14, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", textAlign: "center"
          }}>
            <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700 }}>Visual</h3>
            <pre style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, lineHeight: 2, color: "#334155", display: "inline-block" }}>{`    Vehicle
       │
      Car

  MRO: Car → Vehicle → object`}</pre>
          </div>
        )}

        {active === "multilevel" && (
          <div style={{
            background: "white", borderRadius: 14, padding: "20px 22px",
            marginBottom: 14, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", textAlign: "center"
          }}>
            <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700 }}>Visual</h3>
            <pre style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, lineHeight: 2, color: "#334155", display: "inline-block" }}>{`    Animal
       │
      Dog
       │
     Puppy

  MRO: Puppy → Dog → Animal → object`}</pre>
          </div>
        )}

        {active === "multi" && (
          <div style={{
            background: "white", borderRadius: 14, padding: "20px 22px",
            marginBottom: 14, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", textAlign: "center"
          }}>
            <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700 }}>Visual</h3>
            <pre style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, lineHeight: 2, color: "#334155", display: "inline-block" }}>{`  Flyer    Swimmer
     \\      /
      Duck

  MRO: Duck → Flyer → Swimmer → object`}</pre>
          </div>
        )}

        {active === "hierarchical" && (
          <div style={{
            background: "white", borderRadius: 14, padding: "20px 22px",
            marginBottom: 14, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", textAlign: "center"
          }}>
            <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700 }}>Visual</h3>
            <pre style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, lineHeight: 2, color: "#334155", display: "inline-block" }}>{`       Shape
      /     \\
  Circle   Square

  Circle MRO: Circle → Shape → object
  Square MRO: Square → Shape → object`}</pre>
          </div>
        )}

        {active === "hybrid" && (
          <div style={{
            background: "white", borderRadius: 14, padding: "20px 22px",
            marginBottom: 14, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", textAlign: "center"
          }}>
            <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700 }}>Visual</h3>
            <pre style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, lineHeight: 2, color: "#334155", display: "inline-block" }}>{`       A
      / \\
     B   C
     |
     D
      \\  /
       E

  MRO: E → D → B → C → A → object`}</pre>
          </div>
        )}
      </div>
    </div>
  );
}
